import type { Dispatch } from "redux"
import { performRESTRequest } from "@ng-mw/framework-core"
import { Logger, Cacher } from "@ng-mw/shared-react-components"

import * as favoriteRecipesActions from "./favoriteRecipesActionCreators"
import CancellationToken from "../../../../../helpers/CancellationToken"

let currentlyLoadingArgs = false
let cancellationToken: CancellationToken | null = null

const CACHE_KEY = "allFavoriteRecipes"

export function getAllFavoriteRecipes() {
    return async function (dispatch: Dispatch<any>) {
        if (currentlyLoadingArgs) {
            // onsole.debug("FavoriteRecipes - getAllFavoriteRecipes called! Already running! return")
            return
        }
        cancellationToken?.cancel()
        cancellationToken = new CancellationToken()

        // onsole.debug("FavoriteRecipes - getAllFavoriteRecipes called! Starting call!")

        dispatch(favoriteRecipesActions.getAllFavoriteRecipesLoading())

        try {
            const predicate = {}
            const cached = await Cacher.get(CACHE_KEY, predicate)
            if (cached) {
                // No need to set currentlyLoadingArgs when using cached response
                dispatch(favoriteRecipesActions.getAllFavoriteRecipesFromCache(cached))
            } else {
                currentlyLoadingArgs = true
                const response = await performRESTRequest({
                    path: "/api/stored-recipe-lists/{chainid}",
                    method: "GET",
                })
                currentlyLoadingArgs = false
                // Prevent update from outdated request
                if (cancellationToken.isCancelled()) {
                    // onsole.debug("FavoriteRecipes - getAllFavoriteRecipes cancelled")
                    return
                }
                // onsole.debug("FavoriteRecipes - getAllFavoriteRecipes completed!")

                response.favoredRecipes = response.favoredRecipes.reverse()
                response.purchasedRecipes = response.purchasedRecipes.reverse()

                dispatch(favoriteRecipesActions.getAllFavoriteRecipesSuccess(response))
                // save response data to cache
                Cacher.cache(CACHE_KEY, predicate, response)
            }
        } catch (e: any) {
            dispatch(favoriteRecipesActions.getAllFavoriteRecipesError(e))
            Logger.error("actions/favoriteRecipes/getAllFavoriteRecipes()", e)
        } finally {
            dispatch(favoriteRecipesActions.getAllFavoriteRecipesLoaded())
        }
    }
}

export function unloadFavoriteRecipes() {
    return async function (dispatch: Dispatch<any>) {
        dispatch(favoriteRecipesActions.unloadFavoriteRecipes())
        Cacher.empty(CACHE_KEY)
    }
}

export function createFavoriteRecipes(
    recipeIds: string[],
    purchased: boolean,
    loadFavoritesOnCreate?: boolean,
) {
    return async function (dispatch: Dispatch<any>) {
        dispatch(favoriteRecipesActions.createFavoriteRecipesLoading(recipeIds))

        try {
            const data: string[] = await performRESTRequest({
                path: "/api/stored-recipe-lists/{chainid}",
                method: "POST",
                body: {
                    recipeIds: recipeIds,
                    purchased: purchased,
                },
            })

            dispatch(favoriteRecipesActions.createFavoriteRecipesSuccess(data))
            Cacher.empty(CACHE_KEY)
        } catch (e: any) {
            dispatch(favoriteRecipesActions.createFavoriteRecipesError(e))
            Logger.error("actions/favoriteRecipes/createFavoriteRecipes()", e)
        } finally {
            dispatch(favoriteRecipesActions.createFavoriteRecipesLoaded())

            if (loadFavoritesOnCreate) {
                dispatch(getAllFavoriteRecipes())
            }
        }
    }
}

export function createFavoriteRecipe(
    recipeId: string,
    favorite: boolean,
    loadFavoritesOnCreate?: boolean,
) {
    return async function (dispatch: Dispatch<any>) {
        dispatch(favoriteRecipesActions.createFavoriteRecipeLoading(recipeId))

        try {
            const data: string[] = await performRESTRequest({
                path: "/api/stored-recipe-lists/{chainid}",
                method: "POST",
                body: {
                    recipeIds: [recipeId],
                    favorite: favorite,
                },
            })

            dispatch(favoriteRecipesActions.createFavoriteRecipeSuccess(data))
            Cacher.empty(CACHE_KEY)
        } catch (e: any) {
            dispatch(favoriteRecipesActions.createFavoriteRecipeError(e))
            Logger.error("actions/favoriteRecipes/createFavoriteRecipe()", e)
        } finally {
            dispatch(favoriteRecipesActions.createFavoriteRecipeLoaded())

            if (loadFavoritesOnCreate) {
                dispatch(getAllFavoriteRecipes())
            }
        }
    }
}

export function removeFavoriteRecipe(recipeId: string) {
    return async function (dispatch: Dispatch<any>) {
        dispatch(favoriteRecipesActions.removeFavoriteRecipeLoading(recipeId))

        try {
            const data = await performRESTRequest({
                path: `/api/stored-recipe-lists/{chainid}/${recipeId}`,
                method: "DELETE",
            })

            dispatch(favoriteRecipesActions.removeFavoriteRecipeSuccess(data))
            Cacher.empty(CACHE_KEY)
        } catch (e: any) {
            dispatch(favoriteRecipesActions.removeFavoriteRecipeError(e))
            Logger.error("actions/favoriteRecipes/removeFavoriteRecipe()", e)
        } finally {
            dispatch(favoriteRecipesActions.removeFavoriteRecipeLoaded())
        }
    }
}
